// ============================================================================
// FORMATO REGIONAL DE FECHAS Y HORAS
// ============================================================================
// Espejo conceptual de utils/formatProjectData.js, pero para fechas. Punto
// ÚNICO donde se decide cómo se escribe una fecha en pantalla, en PDF o en
// Excel.
//
// El problema que resuelve
// -----------------------
// Había 21 llamadas de presentación con el patrón incrustado a mano
// ('DD/MM/YYYY', 'DD/MM', 'DD-MM-YYYY hh:mm'...), repartidas en 10 archivos,
// más tres copias del mismo `formattDate()` armando la fecha por concatenación.
// Cambiar el orden de los campos para un proyecto en inglés obligaba a tocar
// las 21. Y `dayjs.locale()` no alcanza: traduce los NOMBRES de mes y día, pero
// no reordena `DD/MM` a `MM/DD` ni cambia el separador.
//
// La solución: formatos SEMÁNTICOS
// --------------------------------
// El código pide "una fecha corta" o "fecha con hora", no un patrón. El patrón
// concreto lo elige este módulo según el Formato Regional del proyecto:
//
//   formatDate(valor, 'date')      es-CL 27/07/2026     en-US 07/27/2026
//   formatDate(valor, 'dateTime')  es-CL 27/07/2026 21:17
//   formatDate(valor, 'long')      es-CL Lunes 27 de julio de 2026
//
// Esto es lo que permite resolver el caso 'dddd DD [de] MMMM [de] YYYY': el
// literal "[de]" es español puro y NO se traduce cambiando el locale de Day.js;
// necesita una plantilla distinta por locale, no una traducción.
//
// Decisión de producto: HORA EN 24h SIEMPRE
// -----------------------------------------
// También en en-US. La app es operativa (riegos, alertas, ventanas de riego) y
// el 24h evita la ambigüedad de un AM/PM mal leído. De paso corrige un bug
// previo: helpers.js formateaba con 'hh:mm' (reloj de 12) SIN AM/PM, con lo que
// las 09:30 y las 21:30 se mostraban idénticas.
//
// Zona horaria: qué hace y qué NO hace este módulo
// ------------------------------------------------
// Este módulo formatea; no convierte husos. Ver `parseProjectDate()` abajo para
// la convención de los timestamps de la API, que está verificada contra datos
// reales y NO es la que sugiere el sufijo "Z".

import dayjs from './dayjs';
import { resolveLocale } from './localeConfig';
import { getActiveLocale, EMPTY_VALUE } from './formatProjectData';

// ----------------------------------------------------------------------------
// Catálogo de formatos
// ----------------------------------------------------------------------------
// Agregar un locale = agregar una entrada acá. Ningún componente cambia.
// Las claves son las MISMAS en todos los locales: si falta una, se cae al
// default y eso se ve en pantalla, así que conviene mantenerlas completas.
const DATE_FORMATS = {
  'es-CL': {
    date: 'DD/MM/YYYY',
    dateShort: 'DD/MM',
    dateTime: 'DD/MM/YYYY HH:mm',
    dateTimeSeconds: 'DD/MM/YYYY HH:mm:ss',
    time: 'HH:mm',
    timeSeconds: 'HH:mm:ss',
    dayMonth: 'DD MMM',
    dayMonthYear: 'DD MMM YYYY',
    monthYear: 'MMMM YYYY',
    year: 'YYYY',
    long: 'dddd DD [de] MMMM [de] YYYY',
    weekdayShort: 'ddd',
    weekdayShortDate: 'ddd DD/MM',
    // Sello de metadatos de reportes (PDF/Excel): "29-07-2026 10:42".
    stamp: 'DD-MM-YYYY HH:mm',
    stampDate: 'DD-MM-YYYY',
    // Patrón de flatpickr (tokens propios de la librería, no de Day.js).
    picker: 'd/m/Y',
    pickerDateTime: 'd/m/Y H:i',
    // numFmt de Excel (tokens de OOXML, no de Day.js). A diferencia de los
    // numFmt NUMÉRICOS —que son locale-independientes y Excel traduce solo—,
    // el ORDEN de los campos de una fecha sí queda fijado en el patrón.
    excel: 'dd-mm-yyyy hh:mm'
  },
  'en-US': {
    date: 'MM/DD/YYYY',
    dateShort: 'MM/DD',
    dateTime: 'MM/DD/YYYY HH:mm',
    dateTimeSeconds: 'MM/DD/YYYY HH:mm:ss',
    time: 'HH:mm',
    timeSeconds: 'HH:mm:ss',
    dayMonth: 'MMM D',
    dayMonthYear: 'MMM D, YYYY',
    monthYear: 'MMMM YYYY',
    year: 'YYYY',
    long: 'dddd, MMMM D, YYYY',
    weekdayShort: 'ddd',
    weekdayShortDate: 'ddd MM/DD',
    stamp: 'MM-DD-YYYY HH:mm',
    stampDate: 'MM-DD-YYYY',
    picker: 'm/d/Y',
    pickerDateTime: 'm/d/Y H:i',
    excel: 'mm-dd-yyyy hh:mm'
  }
};

/** Nombres de formato disponibles (útil para tests y para documentar). */
export const DATE_FORMAT_KINDS = Object.keys(DATE_FORMATS['es-CL']);

/**
 * Patrón concreto para un formato semántico y un locale.
 * Se exporta porque hay consumidores que necesitan el PATRÓN, no el resultado:
 * flatpickr (`dateFormat`/`altFormat`) y el `numFmt` de Excel.
 */
export function getDateFormat(kind = 'date', locale = getActiveLocale()) {
  const tabla = DATE_FORMATS[resolveLocale(locale)] || DATE_FORMATS['es-CL'];
  return tabla[kind] || tabla.date;
}

// ----------------------------------------------------------------------------
// Parseo: la convención real de los timestamps de la API
// ----------------------------------------------------------------------------
// CONVENCIÓN ACTUAL (migración a UTC, agosto 2026)
//
// La API entrega INSTANTES UTC REALES: "2026-07-27T20:22:36.000Z" es UTC de
// verdad, no hora de pared. Para mostrarlo hay que convertirlo al huso DEL
// PROYECTO (ver setProjectTimezone más abajo), no dejarlo en UTC ni pasarlo al
// huso del navegador.
//
// ANTES el sufijo "Z" era cosmético: el valor era la hora local de Chile —para
// todos los proyectos, no la del huso de cada uno— serializada con Z por el
// driver de MySQL. Por eso esta función se implementó con `dayjs.utc()` y sin
// conversión: recuperaba la hora de pared. Esa convención murió con la
// migración; mantenerla ahora mostraría UTC (4 horas corridas en Chile).
//
// >>> INSTANTE vs FECHA CIVIL <<<
// La distinción es crítica y es donde estas migraciones se rompen:
//
//   · Un INSTANTE ("2026-08-02T20:22:36Z", "2026-08-02 20:22:36") se convierte
//     al huso del proyecto.
//   · Una FECHA CIVIL ("2026-08-02", de riegos_programados o de alertas) NO se
//     convierte. Es una fecha de calendario, no un momento: convertirla le
//     cambiaría el DÍA (medianoche UTC del 2 es el 1 a las 20:00 en Chile).
//
// NOTA: los timestamps numéricos de las SERIES DE GRÁFICOS se manejan aparte,
// en utils/chartTime.js.
// ----------------------------------------------------------------------------

// Huso del proyecto activo. Se inyecta desde localeBootstrap (mismo mecanismo
// que setGlobalLocale para los números): así este módulo no importa auth.js y
// no se crea un ciclo.
let husoActivo = null;

/** Fija el huso del proyecto para todo el formateo de fechas. */
export function setProjectTimezone(timezone) {
  husoActivo = timezone && String(timezone).trim() ? String(timezone).trim() : null;
}

// (Se eliminó getProjectTimezone(): nadie la llamaba y su nombre chocaba con
// el helper homónimo del backend, que hace algo distinto — leer el huso de la
// BD. El huso activo del cliente sale de auth.getProjectTimezoneConfig().)

const SOLO_FECHA = /^\d{4}-\d{2}-\d{2}$/;

function aHusoDelProyecto(d) {
  if (!husoActivo) return d;
  try {
    return d.tz(husoActivo);
  } catch {
    return d; // huso inválido: mejor UTC que romper la vista
  }
}

export function parseProjectDate(value) {
  if (value === null || value === undefined || value === '') return null;
  if (dayjs.isDayjs(value)) return value;

  // Date y epoch numérico son instantes: se convierten al huso del proyecto.
  if (value instanceof Date || typeof value === 'number') {
    const d = dayjs(value);
    return d.isValid() ? aHusoDelProyecto(d.utc()) : null;
  }

  const raw = String(value).trim();
  if (!raw) return null;

  const d = dayjs.utc(raw);
  if (!d.isValid()) return null;

  // Fecha civil: se deja tal cual, convertirla le cambiaría el día.
  if (SOLO_FECHA.test(raw)) return d;

  return aHusoDelProyecto(d);
}

/**
 * Nombres de los 7 días de la semana en el Formato Regional del proyecto,
 * EMPEZANDO EN LUNES (que es como arma la grilla el calendario de riegos).
 *
 * Existe porque `dayjs.locale()` no sirve acá: los nombres no acompañan a una
 * fecha concreta, son encabezados de columna. Antes estaban incrustados en
 * español (`['Lu','Ma','Mi','Ju','Vi','Sa','Do']`), así que un proyecto en
 * en-US veía "LU MA MI JU VI SA DO" bajo un título que ya decía "August 2026".
 *
 * @param {string} [locale]
 * @param {object} [options] `style`: 'long' | 'short' | 'narrow' (el de Intl)
 * @returns {string[]} 7 nombres, lunes → domingo
 */
export function weekdayNames(locale = getActiveLocale(), { style = 'short' } = {}) {
  const fmt = new Intl.DateTimeFormat(resolveLocale(locale), { weekday: style, timeZone: 'UTC' });
  // 2021-02-01 fue lunes: siete días consecutivos desde ahí dan la semana
  // completa empezando por lunes, sin depender del primer día del locale.
  return Array.from({ length: 7 }, (_, i) => {
    const t = fmt.format(new Date(Date.UTC(2021, 1, 1 + i))).replace(/\.$/, '');
    return t.charAt(0).toUpperCase() + t.slice(1);
  });
}

/**
 * Formatea una fecha según el Formato Regional del proyecto.
 *
 * @param {string|number|Date|dayjs} value  valor a formatear
 * @param {string} [kind]     formato semántico (ver DATE_FORMAT_KINDS)
 * @param {object} [options]  { locale, fallback }
 * @returns {string} fecha formateada, o `fallback` si no es una fecha válida
 */
export function formatDate(value, kind = 'date', options = {}) {
  const { locale = getActiveLocale(), fallback = EMPTY_VALUE } = options;
  const d = parseProjectDate(value);
  if (!d) return fallback;
  return d.format(getDateFormat(kind, locale));
}

// Alias de los casos más frecuentes, para que el call-site se lea solo.
export const formatDateTime = (value, options) => formatDate(value, 'dateTime', options);
export const formatTime = (value, options) => formatDate(value, 'time', options);
export const formatDateLong = (value, options) => formatDate(value, 'long', options);

/**
 * Sello de fecha/hora para metadatos de reportes (PDF/Excel).
 * `withTime = false` da sólo la fecha ("29-07-2026").
 */
export function formatStampDate(value, { withTime = true, ...options } = {}) {
  return formatDate(value, withTime ? 'stamp' : 'stampDate', options);
}

export default {
  DATE_FORMAT_KINDS,
  getDateFormat,
  weekdayNames,
  parseProjectDate,
  formatDate,
  formatDateTime,
  formatTime,
  formatDateLong,
  formatStampDate
};
